import React, { useEffect, useState } from 'react';
import {
  useParams,
  useNavigate } from 'react-router-dom';
import { doc,
  getDoc,
  collection,
  addDoc } from 'firebase/firestore';
import { Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Link } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { useTheme, alpha } from '@mui/material/styles';
import { Helmet } from 'react-helmet';
import { OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { fetchSupplements } from '../Utils';
import config from '../config';
import SupplementsTable from './SupplementsTable';
import { color } from '../theme/tokens';

function ViewStack() {
  const { stackId } = useParams();
  const [stack, setStack] = useState(null);
  const [supplementMap, setSupplementMap] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [pendingDrawerRestoreInfo, setPendingDrawerRestoreInfo] = useState(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    let isMounted = true;

    async function fetchStackAndSupplements() {
      setLoading(true);
      setError('');
      try {
        const [stackDocSnap, supplementsList] = await Promise.all([
          getDoc(doc(db, 'stacks', stackId)),
          fetchSupplements(config)
        ]);

        const newSupplementMap = {};
        if (Array.isArray(supplementsList)) {
          supplementsList.forEach(sup => {
            newSupplementMap[sup.key] = sup.name;
          });
        } else {
          console.error("ViewStack: Fetched supplements data is not an array:", supplementsList);
        }
        if (isMounted) {
          setSupplementMap(newSupplementMap);
        }

        if (stackDocSnap.exists()) {
          const stackData = stackDocSnap.data();
          if (isMounted) {
            setStack({ ...stackData, id: stackId });
            if (stackData.citations) {
              console.log("Citations found in stack data:", Object.keys(stackData.citations).length, "supplements with citations");
            } else {
              console.log("No citations field found in stack data.");
            }
          }
        } else {
          if (isMounted) {
            setError('Unable to retrieve stack');
          }
        }
      } catch (err) {
        console.error('Error fetching stack data or supplements:', err);
        if (isMounted) {
          setError('Unable to retrieve stack information');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchStackAndSupplements();

    return () => {
      isMounted = false;
    };
  }, [stackId]);

  const handleAddToMyStack = async () => {
    setLoading(true);
    setError('');
    try {
      const user = auth.currentUser;

      if (!user) {
        setError('You need to be logged in to add a stack to your dashboard. Redirecting to login page...');

        setTimeout(() => {
          navigate('/login', { state: { from: `/view-stack/${stackId}` } });
        }, 3000);
      } else {
        if (stack) {
          if (stack.userId === user.uid) {
            setError('This stack was created by you.');
            setLoading(false);
          } else {
            const { healthGoalsRef, notesRef, ...stackToCopy } = stack;
            
            const newStack = {
              ...stackToCopy,
              userId: user.uid,
              name: `${stack.name}`,
              createdBy: stack.userId,
              isPublic: false,
              origin: 'shared',
              createdAt: new Date(),
              updatedAt: new Date(),
              healthGoalsRef: null,
              notesRef: null
            };
            
            delete newStack.jsCreatedAt;
            delete newStack.jsUpdatedAt;
            delete newStack.id;

            const docRef = await addDoc(collection(db, 'stacks'), newStack);
            
            setError(`Stack "${newStack.name}" added to your dashboard!`);
            setLoading(false);
            
            setTimeout(() => {
              navigate('/dashboard');
            }, 1500);
          }
        }
      }
    } catch (err) {
      console.error('Error adding stack:', err);
      setError('Failed to add stack to your dashboard.');
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    
    if (date.toDate) {
      date = date.toDate();
    }
    
    if (typeof date === 'string') {
      date = new Date(date);
    }
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    try {
      return new Date(date).toLocaleDateString(undefined, options);
    } catch (error) {
      return 'Unknown date';
    }
  };

  const handleMainLinkClick = (supplementId, e) => {
    if (e) e.preventDefault();
    navigate(`/supplements/${supplementId}`);
  };

  const handleDrawerLinkClick = (articleId, supplementId, drawerScroll) => {
    navigate(`/supplements/${supplementId}/research/${articleId}`);
  };

  const handleDrawerRestored = () => {
    setPendingDrawerRestoreInfo(null);
  };

  const getStackId = () => {
    return stackId;
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="sm" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !stack) {
    return (
      <Container component="main" maxWidth="sm" sx={{ mt: 8, mb:20 }}>
        <Typography variant="h6" color="error" textAlign="center">
          {error}
        </Typography>
      </Container>
    );
  }

  if (!stack) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{stack.name} - Stack Details</title>
        <meta property="og:title" content={stack.name} />
        <meta property="og:description" content="View the details of this supplement stack." />
        <meta property="og:type" content="website" />
      </Helmet>
      <Container component="main" maxWidth="md" sx={{ mt: 6, mb: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{ 
            width: '100%', 
            maxWidth: '37.5rem',
            background: alpha(color.accent, 0.05),
            borderRadius: '0.75rem',
            padding: { xs: 2, sm: 2.5 },
            boxShadow: '0 0.0625rem 0.5rem rgba(0,0,0,0.05)',
            border: `0.0625rem solid ${alpha(color.accent, 0.15)}`,
            mb: 0.5
          }}>
            <Typography 
              variant="h5" 
              component="h1" 
              sx={{ 
                textAlign: 'center', 
                fontWeight: 600,
                color: color.evergreen,
                fontSize: { xs: '1.25rem', sm: '1.375rem' },
                lineHeight: 1.3,
                mb: 1
              }}
            >
              {stack.name}
            </Typography>
            
            {stack.description && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1.5, 
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: 'text.secondary',
                  maxWidth: '30rem',
                  mx: 'auto'
                }}
              >
                {stack.description}
              </Typography>
            )}
            
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                fontSize: '0.75rem',
                display: 'block',
                textAlign: 'center'
              }}
            >
              Last updated: {formatDate(stack.updatedAt || stack.createdAt)}
            </Typography>
          </Box>
          
          <Box sx={{ width: '100%', maxWidth: '37.5rem' }}>
            {stack.supplements && stack.supplements.length > 0 && (
              <SupplementsTable
                selectedSupplements={stack.supplements}
                setSelectedSupplements={() => {}}
                citations={stack.citations || {}} 
                getStackId={getStackId}
                handleMainLinkClick={handleMainLinkClick}
                handleDrawerLinkClick={handleDrawerLinkClick}
                pendingDrawerRestoreInfo={pendingDrawerRestoreInfo}
                onDrawerRestored={handleDrawerRestored}
                stackVersion={stack.version}
                readOnly={true}
                // Permission only — `hasAnalysis` inside the table still gates
                // the two-chip row. Without this, a saved analyse stack reopens
                // as a single-chip list and the study dose disappears (STACK-544).
                showAnalysis={true}
              />
            )}
          </Box>
          
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1.5, sm: 2 },
            mt: 1.5,
            width: '100%',
            maxWidth: '37.5rem'
          }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddToMyStack}
              disabled={loading || error === 'This stack was created by you.'}
              sx={{
                borderRadius: '0.5rem',
                padding: { xs: '0.5rem 1rem', sm: '0.5rem 1.25rem' },
                width: { xs: '16rem', sm: '15rem' },
                height: '2.75rem',
                fontWeight: 500,
                fontSize: '0.875rem',
                textTransform: 'none',
              }}
            >
              {loading && !error ? 'Adding...' : 'Customize & Add to Dashboard'}
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              href="/create-stack"
              sx={{
                borderRadius: '0.5rem',
                padding: { xs: '0.5rem 1rem', sm: '0.5rem 1.25rem' },
                width: { xs: '16rem', sm: '15rem' },
                height: '2.75rem',
                fontWeight: 500,
                fontSize: '0.875rem',
                textTransform: 'none',
              }}
            >
              Create your own Stack
            </Button>
          </Box>
          
          {error && (
            <Typography 
              variant="body2" 
              color={error.includes('added') ? 'success.main' : 'error'} 
              sx={{ 
                mt: 1,
                padding: '0.625rem 1rem',
                borderRadius: '0.5rem',
                width: '100%',
                maxWidth: '37.5rem',
                textAlign: 'center',
                fontSize: '0.875rem',
                backgroundColor: error.includes('added') ? 'rgba(46, 125, 50, 0.05)' : alpha(color.destructive, 0.05),
                border: `0.0625rem solid ${error.includes('added') ? 'rgba(46, 125, 50, 0.1)' : alpha(color.destructive, 0.1)}`
              }}
            >
              {error}
            </Typography>
          )}
        </Box>
      </Container>
    </>
  );
}

export default ViewStack;